/**
 * useSelectedWorkspace — contributor-portal wrapper over the platform-level
 * `useHolderSwitcher` (`@/components/HolderSwitcher`).
 *
 * The Workspace shared asset library (ADR-019) is owned by the existing
 * Vault org Holder, so "which workspace am I acting in" is the same
 * selection the global header chip drives. This wrapper only projects that
 * selection into the shape the asset-library page needs: the org Holder id
 * (null when Personal or a guild space is selected — neither can own a
 * library), its display name, and the caller's org role.
 *
 * Mirrors `document-sign/hooks/useSelectedHolder.ts`; guild spaces are
 * deliberately NOT opted in (a guild cannot hold an asset library).
 */

import { useHolderSwitcher, type HolderSwitcherOption } from '@/components/HolderSwitcher';
import type { WorkspaceRole } from '@/lib/api';

export interface SelectedWorkspace {
  /** The selected org Holder id, or null when Personal / nothing is selected. */
  holderId: string | null;
  displayName: string | null;
  /** The caller's role from the membership list; the server re-derives on every call. */
  role: WorkspaceRole | null;
  /** True once the switcher has resolved to an org workspace. */
  hasOrgSelected: boolean;
  /** Every org workspace the caller could switch to (drives the empty-state picker). */
  orgOptions: HolderSwitcherOption[];
  setHolderId: (next: string | null) => void;
  isLoading: boolean;
  isError: boolean;
}

export function useSelectedWorkspace(): SelectedWorkspace {
  const switcher = useHolderSwitcher({ defaultToPersonal: true });
  const org = switcher.selectedOption?.holderType === 'org' ? switcher.selectedOption : null;
  return {
    holderId: org?.holderId ?? null,
    displayName: org?.displayName ?? null,
    role: org && org.role !== 'self' ? org.role : null,
    hasOrgSelected: org !== null,
    orgOptions: switcher.options.filter((o) => o.holderType === 'org'),
    setHolderId: switcher.setSelectedHolderId,
    isLoading: switcher.isLoading,
    isError: switcher.isError,
  };
}
